import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { State } from "../reducer";
import { createAccountAPI } from "../api/auth.api";
import { toast } from "react-toastify";
import wordmark from "../assets/images/woxa-cafe-wordmark.png";

const CreateAccount = () => {
  const navigate = useNavigate();
  const user = useSelector((state: State) => state.user);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [peek, setPeek] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);

    const checkEmail = email.split("@")[1];
    if (checkEmail !== "woxacorp.com") {
      setErrorMsg("Registration only accepts @woxacorp.com emails.");
      return;
    }



    setIsLoading(true);
    const payload = { name: username, password: password, email: email };
    try {
      await createAccountAPI(payload);
      toast.success("Account created successfully! You can log in now.");
      navigate("/login");
    } catch (err: any) {
      const status = err?.response?.status as number | undefined;
      const serverMsg = err?.response?.data?.message as string | undefined;
      
      if (status === 409) {
        setErrorMsg(serverMsg ?? "This email is already registered.");
      } else if (status === 400) {
        setErrorMsg(serverMsg ?? "Invalid email format or password requirements.");
      } else if (status === 429) {
        setErrorMsg("Too many attempts. Please try again in a minute.");
      } else if (status && status >= 500) {
        setErrorMsg("Server error. Please try again shortly.");
      } else if (err?.code === "ERR_NETWORK" || err?.message === "Network Error") {
        setErrorMsg("Can't reach the server. Check your internet connection.");
      } else {
        setErrorMsg(serverMsg ?? "Failed to create account. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user.login) navigate("/statusOrder");
  }, [user.login, navigate]);

  return (
    <div className="min-h-screen bg-white font-sans relative overflow-hidden">
      {/* === HERO PANEL — matching login page visual structure === */}
      <div className="relative bg-hero text-hero-on px-5 pt-[44px] pb-[80px] overflow-hidden rounded-b-[40px]">
        {/* Decorative dot grid */}
        <svg
          aria-hidden
          className="absolute inset-0 w-full h-full opacity-[0.07] pointer-events-none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <pattern id="dotgrid" x="0" y="0" width="22" height="22" patternUnits="userSpaceOnUse">
              <circle cx="1.5" cy="1.5" r="1.5" fill="white" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#dotgrid)" />
        </svg>

        {/* Floating coffee marks */}
        <svg aria-hidden className="absolute top-6 right-6 w-[120px] h-[120px] opacity-10" viewBox="0 0 120 120" fill="none">
          <circle cx="60" cy="60" r="58" stroke="white" strokeWidth="1" strokeDasharray="2 4"/>
          <circle cx="60" cy="60" r="40" stroke="white" strokeWidth="1"/>
          <path d="M40 50h35a10 10 0 0 1 0 20h-3M44 70h26v6a8 8 0 0 1-8 8H52a8 8 0 0 1-8-8V50z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>

        <div className="relative">
          {/* Wordmark */}
          <div className="mb-[64px]">
            <img
              src={wordmark}
              alt="Woxa Cafe"
              className="h-[44px] w-auto"
              style={{ filter: "invert(1)" }}
            />
          </div>

          {/* Headline */}
          <p className="eyebrow-v2 text-hero-muted mb-3">Join us today</p>
          <h1 className="font-display font-black text-[40px] leading-[1.05] tracking-tightest">
            Create an<br/>account.
          </h1>
          <p className="text-base text-hero-muted mt-3 max-w-[280px]">
            Create a Woxa Cafe account to start ordering drinks and tracking your cups.
          </p>
        </div>
      </div>

      {/* === FORM CARD — overlaps hero === */}
      <div className="px-5 -mt-[48px] relative z-10">
        <div className="bg-white rounded-3xl border border-ink-100 shadow-floaty px-6 pt-[28px] pb-[24px] max-w-md mx-auto">
          <form onSubmit={handleSubmit} className="space-y-[12px]">
            
            <div className="field-v2">
              <input
                id="username"
                type="text"
                required
                maxLength={20}
                placeholder=" "
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
              <label htmlFor="username">Full Name</label>
            </div>

            <div className="field-v2">
              <input
                id="email"
                type="email"
                required
                placeholder=" "
                value={email}
                onChange={(e) => setEmail(e.target.value.replace(/\s/g, ""))}
                onKeyDown={(e) => e.key === " " && e.preventDefault()}
              />
              <label htmlFor="email">Email (@woxacorp.com)</label>
            </div>

            <div className="field-v2 pr-[40px]">
              <input
                id="password"
                type={peek ? "text" : "password"}
                required
                placeholder=" "
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <label htmlFor="password">Password</label>
              <button
                type="button"
                onClick={() => setPeek(!peek)}
                className="absolute right-0 bottom-1 h-9 w-9 rounded-full flex items-center justify-center text-ink-400 hover:text-ink-1000 transition"
                aria-label={peek ? "Hide password" : "Show password"}
              >
                {peek ? (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65"/></svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"/><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                )}
              </button>
            </div>

            {errorMsg && (
              <div className="pt-2 text-sm text-ink-1000 font-medium flex items-center gap-2">
                <svg className="w-[16px] h-[16px] flex-shrink-0 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                </svg>
                {errorMsg}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading || !username || !email || !password}
              className="btn-v2-primary btn-v2-lg w-full mt-[24px]"
            >
              {isLoading ? (
                <>
                  <svg className="w-[16px] h-[16px] animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25"/>
                    <path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                  </svg>
                  Creating account
                </>
              ) : (
                <>
                  Create Account
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3"/>
                  </svg>
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center text-sm font-semibold">
            <Link to="/login" className="text-ink-1000 underline underline-offset-4 hover:text-ink-700 transition">
              Already have an account? Sign in
            </Link>
          </div>
        </div>
      </div>

      {/* === FOOTER === */}
      <div className="px-5 pb-[40px] mt-[64px] max-w-md mx-auto text-center space-y-2 border-t border-ink-100 pt-[24px]">
        <p className="text-xs text-ink-500">
          Need help? Contact{" "}
          <button
            type="button"
            onClick={() => navigate("/team-credit")}
            className="text-ink-1000 font-semibold underline underline-offset-4"
          >
            Dek Due Crew
          </button>
        </p>
        <p className="text-[11px] text-ink-400">
          © {new Date().getFullYear()} Woxa Corp · Brewed with care
        </p>
      </div>
    </div>
  );
};

export default CreateAccount;
